<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div v-for="field in fields" :key="field.name" class="form-field">
    <label :for="field.name">
      {{ field.description }}
      <span v-if="requiredFields.includes(field.name)" class="required">
        *</span
      >
      <i
        v-if="autoExtractedFields.has(field.name)"
        class="ri-sparkling-line auto-extracted-icon"
        v-tooltip.top="t('classificationPlan.autoExtracted')"
      />
    </label>
    <PrimeDatePicker
      v-if="field.input === 'date'"
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      @update:modelValue="updateField(field.name, $event)"
    />
    <PrimeDatePicker
      v-else-if="field.input === 'datetime'"
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      showTime
      @update:modelValue="updateField(field.name, $event)"
    />
    <PrimeInputNumber
      v-else-if="field.type === 'number'"
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      :min="field.min"
      :max="field.max"
      @update:modelValue="updateField(field.name, $event)"
    />
    <PrimeKnob
      v-else-if="field.input === 'knob'"
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      @update:modelValue="updateField(field.name, $event)"
    />
    <PrimeCheckbox
      v-else-if="field.input === 'boolean'"
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      :binary="true"
      @update:modelValue="updateField(field.name, $event)"
    />
    <PrimeToggleSwitch
      v-else-if="field.input === 'toggle'"
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      @update:modelValue="updateField(field.name, $event)"
    />
    <PrimeDropdown
      v-else-if="field.input === 'list'"
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      :options="field.values || []"
      :placeholder="field.description"
      @update:modelValue="updateField(field.name, $event)"
    />
    <PrimeDropdown
      v-else-if="field.input === 'search'"
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      :options="getSearchOptions(field.name)"
      :loading="isSearchLoading(field.name)"
      :placeholder="t('classificationPlan.searchPlaceholder')"
      @update:modelValue="updateField(field.name, $event)"
      @focus="$emit('load-search', field)"
    />
    <PrimeMultiSelect
      v-else-if="field.input === 'multilist'"
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      :options="field.values || []"
      :placeholder="field.description"
      @update:modelValue="updateField(field.name, $event)"
    />
    <PrimeSelectButton
      v-else-if="field.input === 'select'"
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      :options="field.values || []"
      @update:modelValue="updateField(field.name, $event)"
    />
    <PrimeDropdown
      v-else-if="field.input === 'dictionary'"
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      :options="getDictionaryEntries(field.source)"
      option-label="value"
      option-value="key"
      :placeholder="field.description"
      @update:modelValue="updateField(field.name, $event)"
    />
    <PrimeDropdown
      v-else-if="field.input === 'lexicon'"
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      :options="getLexiconValues(field.source)"
      :placeholder="field.description"
      @update:modelValue="updateField(field.name, $event)"
    />
    <PrimeInputChips
      v-else-if="field.input === 'chips'"
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      @update:modelValue="updateField(field.name, $event)"
    />
    <PrimeRating
      v-else-if="field.input === 'rating'"
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      @update:modelValue="updateField(field.name, $event)"
    />
    <PrimePassword
      v-else-if="field.input === 'password'"
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      @update:modelValue="updateField(field.name, $event)"
    />
    <PrimeTextarea
      v-else-if="field.input === 'textarea'"
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      @update:modelValue="updateField(field.name, $event)"
    />
    <div v-else-if="field.input === 'parent'" class="parent-field-wrapper">
      <PrimeDropdown
        v-if="
          parentFieldOptions[field.name]?.length > 0 && !modelValue[field.name]
        "
        :id="field.name"
        :modelValue="modelValue[field.name]"
        :options="parentFieldOptions[field.name]"
        optionLabel="label"
        optionValue="value"
        :placeholder="t('classificationPlan.selectParentValue')"
        class="w-full"
        @update:modelValue="updateField(field.name, $event)"
      />
      <PrimeInputText
        v-else
        :id="field.name"
        :modelValue="modelValue[field.name]"
        disabled
        class="w-full"
      />
      <small
        v-if="
          !modelValue[field.name] && !parentFieldOptions[field.name]?.length
        "
        class="p-error"
      >
        {{ t("classificationPlan.parentFieldNotFound") }}
      </small>
    </div>
    <PrimeInputText
      v-else
      :id="field.name"
      :modelValue="modelValue[field.name]"
      :disabled="disabledFields.has(field.name)"
      :pattern="field.regex"
      @update:modelValue="updateField(field.name, $event)"
      @input="$emit('validate', field, modelValue[field.name])"
    />
    <small
      v-if="
        field.type === 'number' &&
        (field.min !== undefined || field.max !== undefined)
      "
      >{{ t("classificationPlan.min") }}:
      {{ field.min ?? t("classificationPlan.na") }},
      {{ t("classificationPlan.max") }}:
      {{ field.max ?? t("classificationPlan.na") }}</small
    >
    <small v-if="field.type === 'string' && field.regex"
      >{{ t("classificationPlan.constraint") }}: {{ field.regex }}</small
    >
    <small
      v-if="
        field.default !== undefined &&
        field.default !== null &&
        field.default !== ''
      "
      >{{ t("classificationPlan.defaultValue") }}: {{ field.default }}</small
    >
    <small v-if="validationErrors[field.name]" class="p-error">{{
      validationErrors[field.name]
    }}</small>
  </div>
</template>

<script>
import DatePicker from "primevue/datepicker";
import InputNumber from "primevue/inputnumber";
import Knob from "primevue/knob";
import Checkbox from "primevue/checkbox";
import ToggleSwitch from "primevue/toggleswitch";
import Dropdown from "primevue/dropdown";
import MultiSelect from "primevue/multiselect";
import SelectButton from "primevue/selectbutton";
import InputChips from "primevue/inputchips";
import Rating from "primevue/rating";
import Password from "primevue/password";
import Textarea from "primevue/textarea";
import InputText from "primevue/inputtext";
import { useI18n } from "vue-i18n";

export default {
  name: "MetadataFormFields",
  components: {
    PrimeDatePicker: DatePicker,
    PrimeInputNumber: InputNumber,
    PrimeKnob: Knob,
    PrimeCheckbox: Checkbox,
    PrimeToggleSwitch: ToggleSwitch,
    PrimeDropdown: Dropdown,
    PrimeMultiSelect: MultiSelect,
    PrimeSelectButton: SelectButton,
    PrimeInputChips: InputChips,
    PrimeRating: Rating,
    PrimePassword: Password,
    PrimeTextarea: Textarea,
    PrimeInputText: InputText,
  },
  props: {
    fields: { type: Array, required: true },
    modelValue: { type: Object, required: true },
    requiredFields: { type: Array, default: () => [] },
    disabledFields: { type: Set, default: () => new Set() },
    validationErrors: { type: Object, default: () => ({}) },
    getSearchOptions: { type: Function, default: () => [] },
    isSearchLoading: { type: Function, default: () => false },
    dictionaries: { type: Array, default: () => [] },
    lexicons: { type: Array, default: () => [] },
    autoExtractedFields: { type: Set, default: () => new Set() },
    parentFieldOptions: { type: Object, default: () => ({}) },
  },
  emits: ["update:modelValue", "load-search", "validate"],
  setup(props, { emit }) {
    const { t } = useI18n();

    const updateField = (fieldName, value) => {
      emit("update:modelValue", { ...props.modelValue, [fieldName]: value });
    };

    const getDictionaryEntries = (sourceId) => {
      const dict = props.dictionaries.find((d) => d.id === sourceId);
      return dict ? dict.entries || [] : [];
    };

    const getLexiconValues = (sourceId) => {
      const lex = props.lexicons.find((l) => l.id === sourceId);
      return lex ? lex.values || [] : [];
    };

    return { t, updateField, getDictionaryEntries, getLexiconValues };
  },
};
</script>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 1rem;
}

.auto-extracted-icon {
  margin-left: 0.25rem;
  color: var(--p-primary-color);
  cursor: help;
}
</style>
